import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../src/config";
import Sidebar from "../Sidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";

// Razorpay's checkout.js used to be hardcoded in index.html and loaded on
// every single page, even though this embedded-checkout popup is the ONLY
// place in the whole frontend that actually uses window.Razorpay — guest
// bookings use Razorpay's hosted Payment Links instead (a plain redirect
// that needs no script at all). Loading it lazily here means the ~99% of
// visitors who are guests, not owners managing a subscription, never pay
// for Razorpay's full checkout bundle just to browse stays.
let razorpayScriptPromise = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = resolve;
    script.onerror = () => {
      razorpayScriptPromise = null; // allow retrying on a later attempt
      reject(new Error("Failed to load Razorpay checkout script"));
    };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const RevenueSubscriptionSection = () => {

  const token = localStorage.getItem("accessToken");

  const [revenueModels, setRevenueModels] = useState([]);
  const [fixedPlans, setFixedPlans] = useState([]);

  const [selectedRevenueModel, setSelectedRevenueModel] = useState(null);
  const [selectedFixedPlan, setSelectedFixedPlan] = useState("");
  const [commission, setCommission] = useState("");

  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasBankAccount, setHasBankAccount] = useState(true); // assume true until checked, avoids a flash of the warning banner

  const [loading, setLoading] = useState(false);          // save loader
  const [pageLoading, setPageLoading] = useState(true);  // page loader

  /* =====================================
     LOAD OPTIONS + CURRENT SUBSCRIPTION
  ======================================*/
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setPageLoading(true);
      await loadRevenueOptions();
      await loadCurrentSubscription();
      await checkBankAccount();
    } finally {
      setPageLoading(false);
    }
  };

  const checkBankAccount = async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}auth/bank-account/get`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasBankAccount((res.data.data || []).length > 0);
    } catch (error) {
      // if this check itself fails, don't block the page on it — the
      // backend still enforces the real rule on save regardless
      setHasBankAccount(true);
    }
  };

  const loadRevenueOptions = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}auth/revenue/options`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setRevenueModels(res.data.data.revenueModels);
      setFixedPlans(res.data.data.fixedPlans);

    } catch (error) {
      toast.error("Failed to load revenue options");
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}auth/revenue/my-subscription`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const sub = res.data.data;
      if (!sub) return;

      setCurrentSubscription(sub);

      if (sub.model_type === "fixed") {
        const model = revenueModels.find(m => m.model_type === "fixed");
        if (model) setSelectedRevenueModel(model);
        if (sub.fixed?.plan) setSelectedFixedPlan(sub.fixed.plan.id);
      }

      if (sub.model_type === "commission") {
        const model = revenueModels.find(m => m.model_type === "commission");
        if (model) setSelectedRevenueModel(model);
        setCommission(sub.commission.commission_percentage);
      }

    } catch (error) {
      // no active subscription
    }
  };

  /* =====================================
     SAVE SUBSCRIPTION
  ======================================*/
  // const saveSubscription = async () => {
  //   try {
  //     if (!selectedRevenueModel) {
  //       toast.warning("Please select a revenue model");
  //       return;
  //     }

  //     setLoading(true);

  //     await axios.post(
  //       `${API_BASE_URL}auth/revenue/subscribe`,
  //       {
  //         mst_revenue_model_id: selectedRevenueModel.id,
  //         fixed_fee_plan_id:
  //           selectedRevenueModel.model_type === "fixed"
  //             ? selectedFixedPlan
  //             : null,
  //         commission_percentage:
  //           selectedRevenueModel.model_type === "commission"
  //             ? commission
  //             : null
  //       },
  //       {
  //         headers: { Authorization: `Bearer ${token}` }
  //       }
  //     );



  //     toast.success("Revenue subscription saved successfully");
  //     loadCurrentSubscription();

  //   } catch (error) {
  //     toast.error(
  //       error.response?.data?.message ||
  //       "Failed to save subscription"
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const saveSubscription = async () => {
    try {
      if (!hasBankAccount) {
        toast.warning("Please add a bank account before selecting a revenue model");
        return;
      }

      if (!selectedRevenueModel) {
        toast.warning("Please select a revenue model");
        return;
      }

      //. validation for fixed plan
      if (
        selectedRevenueModel.model_type === "fixed" &&
        !selectedFixedPlan
      ) {
        toast.warning("Please select a fixed plan");
        return;
      }

      setLoading(true);

      const res = await axios.post(
        `${API_BASE_URL}auth/revenue/subscribe`,
        {
          mst_revenue_model_id: selectedRevenueModel.id,
          fixed_fee_plan_id:
            selectedRevenueModel.model_type === "fixed"
              ? selectedFixedPlan
              : null,
          commission_percentage:
            selectedRevenueModel.model_type === "commission"
              ? commission
              : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const selectedPlanDetails = fixedPlans.find(
        (p) => p.id == selectedFixedPlan
      );
      /* =====================================
         🔥 FIXED PLAN → OPEN RAZORPAY POPUP
      ======================================*/
      if (res.data.subscription_id) {
        toast.info("Opening payment popup...");

        await loadRazorpayScript();

        const options = {
          key: res.data.key,
          subscription_id: res.data.subscription_id,

          name: "GuestLinkup",

          description:
            selectedPlanDetails
              ? `${selectedPlanDetails.plan_name} - ₹${selectedPlanDetails.monthly_fee}/month`
              : "Subscription Payment",

          handler: function (response) {
            console.log("Payment Success:", response);

            toast.success("Payment successful!");

            loadCurrentSubscription();
          },

          modal: {
            ondismiss: function () {
              toast.warning("Payment cancelled");
            }
          },

          theme: {
            color: "#3399cc"
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();

        return;
      }

      /* =====================================
        . COMMISSION MODEL (NO PAYMENT)
      ======================================*/
      toast.success(
        res.data.message || "Subscription activated successfully"
      );

      await loadCurrentSubscription();

    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.message ||
        "Failed to save subscription"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =====================================
     PAGE LOADER
  ======================================*/
  if (pageLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }

  return (

    <section className="profilecontainer">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="profileinner">
              <div className="row">

                <Sidebar />

                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-9 col-sm-12 col-12">
                  <div className="profilerightouter">
                    <h3>Revenue Subscription</h3>

                    {/* ================= CURRENT SUBSCRIPTION ================= */}
                    {currentSubscription && (
                      <div className="mb-4 subscriptiontext">
                        <h6 className="fw-bold mb-2">Current Subscription</h6>

                        <p className="mb-1">
                          <strong>Revenue Model:</strong>{" "}
                          {currentSubscription.model_type === "fixed"
                            ? "Fixed"
                            : "Commission"}
                        </p>
                        {currentSubscription.model_type === "fixed" && (
                          <>
                            <p className="mb-1">
                              <strong>Plan:</strong>{" "}
                              {currentSubscription.fixed.plan.plan_name}
                            </p>

                            <p className="mb-1">
                              <strong>Monthly Fee:</strong> ₹
                              {currentSubscription.fixed.plan.monthly_fee}
                            </p>

                            <p className="mb-1">
                              <strong>Properties Allowed:</strong>{" "}
                              {currentSubscription.fixed.plan.min_properties}
                              {" - "}
                              {currentSubscription.fixed.plan.max_properties
                                ? currentSubscription.fixed.plan.max_properties
                                : "No limit"}
                            </p>

                            <p className="mb-1">
                              <strong>Billing:</strong> MONTHLY
                            </p>
                          </>
                        )}


                        {currentSubscription.model_type === "commission" && (
                          <>
                            <p className="mb-1">
                              <strong>Commission:</strong>{" "}
                              {currentSubscription.commission.commission_percentage}%
                            </p>

                            <p className="mb-1">
                              <strong>Billing:</strong> PER BOOKING
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    <hr />

                    {/* ================= SELECT REVENUE MODEL ================= */}
                    {revenueModels.map(model => (
                      <div key={model.id} className="form-check mt-3">
                        <input
                          type="radio"
                          className="form-check-input"
                          checked={selectedRevenueModel?.id === model.id}
                          // onChange={() => {
                          //   setSelectedRevenueModel(model);
                          //   setSelectedFixedPlan("");
                          //   setCommission("");
                          // }}

                          onChange={() => {
                            setSelectedRevenueModel(model);
                            setSelectedFixedPlan("");

                            if (model.model_type === "commission") {
                              setCommission(model.default_commission || "");
                            } else {
                              setCommission("");
                            }
                          }}

                        />
                        <label className="form-check-label">
                          {model.title}
                        </label>
                      </div>
                    ))}

                    {/* ================= FIXED PLANS ================= */}
                    {selectedRevenueModel?.model_type === "fixed" && (
                      <div className="form-group mt-3">
                        <select
                          className="form-select"
                          value={selectedFixedPlan}
                          onChange={(e) => setSelectedFixedPlan(e.target.value)}
                        >
                          <option value="">Select Fixed Plan</option>
                          {fixedPlans
                            .sort((a, b) => a.id - b.id)
                            .map(plan => (
                              <option key={plan.id} value={plan.id}>
                                {plan.monthly_fee === "0.00"
                                  ? "Only one month"
                                  : `₹${plan.monthly_fee}/Month`} — {plan.plan_name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    {/* ================= COMMISSION ================= */}
                    {selectedRevenueModel?.model_type === "commission" && (
                      <div className="form-group mt-3">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Commission %"
                          value={commission}
                          min={selectedRevenueModel.min_commission}
                          max={selectedRevenueModel.max_commission}
                          onChange={(e) => setCommission(e.target.value)}
                          readOnly
                        />

                        <small className="text-muted">
                          Allowed range: {selectedRevenueModel.min_commission}% –{" "}
                          {selectedRevenueModel.max_commission}%
                        </small>
                      </div>
                    )}

                    {!hasBankAccount && (
                      <div className="alert alert-warning mt-3" role="alert">
                        You need to add a bank account before selecting a revenue model.{" "}
                        <a href="/my-banks">Add one now</a>.
                      </div>
                    )}

                    <button
                      className="theme-btn mt-3"
                      onClick={saveSubscription}
                      disabled={loading || !hasBankAccount}
                    >
                      {loading ? "Saving..." : "Save Subscription"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevenueSubscriptionSection;
